import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { normalizeNodeId } from '@gt/para/shared/para-node-id.util';
import { ParaApiRootService } from './para-api-root.service';
import { ParaOrgStateService } from './para-org-state.service';
import { ParaSelectedAssessmentService } from './para-selected-assessment.service';

export interface ParaAssessmentContextPhaseShape {
	phaseId: string;
	sortOrder: number;
	/** @deprecated Prefer sortOrder; retained for older callers. */
	phaseNumber?: number;
	shortLabel: string;
	longLabel: string;
	/** Optional phase accent (hex, rgb, etc.) */
	color?: string | null;
	itemCount: number;
	domains?: Array<{
		domainId: string;
		shortLabel: string;
		longLabel: string;
		color?: string | null;
		itemCount: number;
	}>;
}

/** Merged stop-rule row from `getAssessmentContext` (`Para_Stop_Rule` + attachment). */
export interface ParaAssessmentContextStopRule {
	ruleId: string;
	mode: string;
	params?: Record<string, unknown>;
}

/** Supplementary scorer copy per LMS header + challenge (`Para_Lms_Header_Supplement`). */
export interface ParaLmsHeaderSupplementDto {
	lmsHeaderId: string;
	headerLabel: string;
	supplementaryText: string;
	paraAssessmentItemId?: number | null;
	questionNumber?: number | null;
	paraTestQuestionId?: number | null;
	sortOrder?: number;
}

export interface ParaAssessmentContextDto {
	assessment: { assessmentId: string; version: number; shortLabel: string; longLabel: string };
	window: { id: string; label?: string; start: string | null; end: string | null };
	shape: { hasDomains: boolean; phases: ParaAssessmentContextPhaseShape[] };
	stopRules?: ParaAssessmentContextStopRule[];
	lmsHeaderSupplements?: ParaLmsHeaderSupplementDto[];
	milestones?: Array<{
		milestoneId: string;
		label: string;
		color?: string | null;
		phaseId: string;
		startChallengeNumber: number;
		endChallengeNumber: number;
		sortOrder: number;
	}>;
}

/**
 * PARA AssessmentContext: assessment-aware shape + active window for dashboards.
 *
 * Backend: GET /para/assessment/getAssessmentContext/nodeId/:id
 */
@Injectable({ providedIn: 'root' })
export class ParaAssessmentContextService {
	private readonly httpByCacheKey = new Map<string, Observable<ParaAssessmentContextDto>>();

	constructor(
		private readonly paraOrgState: ParaOrgStateService,
		private readonly httpClient: HttpClient,
		private readonly paraApi: ParaApiRootService,
		private readonly paraSelectedAssessment: ParaSelectedAssessmentService
	) {
		// When PARA scope changes, drop cached contexts for other nodes.
		this.paraOrgState.selectedParaNodeEntity$
			.pipe(
				filter((data: any) => !!data && data.id != null && data.id !== ''),
				map((data: any) => normalizeNodeId(data.id)),
				filter((id): id is number => id != null),
				distinctUntilChanged()
			)
			.subscribe((nid) => this.pruneHttpCacheExcept(nid));
	}

	getAssessmentContext(nodeId: unknown, assessmentId?: string | null): Observable<ParaAssessmentContextDto | null> {
		const id = normalizeNodeId(nodeId);
		if (id == null) {
			return of(null);
		}

		const aid = (assessmentId || '').trim();
		const cacheKey = aid ? `${id}::${aid}` : String(id);
		let shared = this.httpByCacheKey.get(cacheKey);
		if (!shared) {
			let params = new HttpParams();
			if (aid) {
				params = params.set('assessmentId', aid);
			}
			shared = this.httpClient
				.get<ParaAssessmentContextDto>(this.paraApi.assessmentUrl(`getAssessmentContext/nodeId/${id}`), { params })
				.pipe(shareReplay({ bufferSize: 1, refCount: true }));
			this.httpByCacheKey.set(cacheKey, shared);
		}
		return shared;
	}

	/** AssessmentContext for the currently selected classroom (org-state default). */
	readonly classroomAssessmentContext$: Observable<ParaAssessmentContextDto | null> = this.paraOrgState.selectedClassroom$.pipe(
		distinctUntilChanged((a, b) => normalizeNodeId(a?.id) === normalizeNodeId(b?.id)),
		switchMap((classroom) => {
			const nid = normalizeNodeId(classroom?.id);
			if (nid == null) return of(null);
			return this.paraSelectedAssessment.resolvedAssessmentId$.pipe(switchMap((aid) => this.getAssessmentContext(nid, aid)));
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	/** AssessmentContext for the currently selected PARA scope node (classroom/school/area/district). */
	readonly selectedNodeAssessmentContext$: Observable<ParaAssessmentContextDto | null> = this.paraOrgState.selectedParaNodeEntity$.pipe(
		distinctUntilChanged((a, b) => normalizeNodeId(a?.id) === normalizeNodeId(b?.id)),
		switchMap((node) => {
			const nid = normalizeNodeId(node?.id);
			if (nid == null) return of(null);
			return this.paraSelectedAssessment.resolvedAssessmentId$.pipe(switchMap((aid) => this.getAssessmentContext(nid, aid)));
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	/** Maximum challenge columns from the first phase's itemCount. Throws if invalid or missing. */
	readonly classroomMaxChallengeColumns$: Observable<number> = this.classroomAssessmentContext$.pipe(
		map((ctx) => {
			const first = (ctx?.shape?.phases ?? [])[0];
			const v = first?.itemCount;
			if (v == null || v <= 0) {
				throw new Error('Invalid or missing itemCount in assessment context');
			}
			return v;
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	/** Visible phase IDs from assessment context phases. */
	readonly classroomVisiblePhaseIds$: Observable<string[]> = this.classroomAssessmentContext$.pipe(
		map((ctx) => {
			const phases = ctx?.shape?.phases ?? [];
			return phases.length ? phases.map((p) => p.phaseId).filter((id) => !!id) : [];
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	invalidate(nodeId: unknown): void {
		const id = normalizeNodeId(nodeId);
		if (id == null) return;
		const prefix = `${id}::`;
		for (const k of [...this.httpByCacheKey.keys()]) {
			if (k === String(id) || k.indexOf(prefix) === 0) {
				this.httpByCacheKey.delete(k);
			}
		}
	}

	/** When PARA classroom scope changes, drop cached contexts for other nodes. */
	private pruneHttpCacheExcept(nid: number): void {
		const keep = String(nid);
		const keepPrefix = `${nid}::`;
		for (const k of [...this.httpByCacheKey.keys()]) {
			if (!(k === keep || k.indexOf(keepPrefix) === 0)) {
				this.httpByCacheKey.delete(k);
			}
		}
	}
}
